import { useEffect, useState } from "react";
import { tasksApi } from "@/api";
import type { StaffUser } from "@/types";
import { SelectField } from "@/components/ui";

/** Staff roster for the task Owner select. `staff` is null while loading (and
 *  stays null on failure, with `failed` set) so callers can tell "roster not
 *  available" from an empty list and leave the assignment untouched on submit
 *  rather than accidentally clearing it. */
export function useStaffUsers(): { staff: StaffUser[] | null; failed: boolean } {
  const [staff, setStaff] = useState<StaffUser[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    tasksApi
      .listStaffUsers()
      .then((users) => {
        if (!cancelled) setStaff(users);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { staff, failed };
}

/** Owner (assignee) select shared by both task drawers. Value 0 = unassigned.
 *  Disabled until the roster arrives; on submit the parent must skip the owner
 *  field entirely while `staff` is null so a failed load never clears an
 *  existing assignment. */
export function OwnerSelect({
  staff,
  failed,
  value,
  onChange,
}: {
  staff: StaffUser[] | null;
  failed: boolean;
  value: number;
  onChange: (userId: number) => void;
}) {
  // A task can be assigned to someone who has since left the roster; keep the
  // stored assignment visible instead of letting the select lie "Unassigned".
  const orphaned = staff != null && value > 0 && !staff.some((u) => u.id === value);
  return (
    <SelectField
      label="Owner"
      value={String(value)}
      disabled={staff == null}
      error={failed ? "Couldn't load the staff list." : undefined}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {staff == null ? (
        <option value={String(value)}>{failed ? "—" : "Loading…"}</option>
      ) : (
        <>
          <option value="0">Unassigned</option>
          {staff.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
          {orphaned ? <option value={String(value)}>User #{value}</option> : null}
        </>
      )}
    </SelectField>
  );
}
